import { useState, useEffect, useCallback } from "react";
import { useAdmin } from "@/context/AdminContext";
import {
  loadCustomPages,
  saveCustomPages,
  type CustomPage,
  type PageBlock,
  type BlockType,
} from "@/utils/customPageStore";
import {
  stagingStoreSet,
  stagingStoreNotify,
  getCurrentValueForKey,
} from "@/hooks/useAdminStorage";

const STORAGE_KEY = "ma_custom_pages_v1";

/**
 * useCustomPages
 *
 * A staging-aware hook for custom page state. In admin mode:
 * - All mutations stage changes in memory (not localStorage)
 * - Commit writes to localStorage AND stages for Supabase sync
 * - Reset re-reads from localStorage (used on Discard)
 *
 * In visitor mode: localStorage is read directly, no staging.
 */
export function useCustomPages() {
  const { isAdmin, discardVersion } = useAdmin();

  const load = useCallback((): CustomPage[] => {
    return getCurrentValueForKey<CustomPage[]>(STORAGE_KEY, []);
  }, []);

  const [pages, setPagesState] = useState<CustomPage[]>(load);
  const [stagedPages, setStagedPages] = useState<CustomPage[] | null>(null);

  // Re-read from localStorage when discard is triggered
  useEffect(() => {
    if (discardVersion === 0) return;
    const fresh = load();
    setPagesState(fresh);
    setStagedPages(null);
  }, [discardVersion, load]);

  // Active pages: staged (unsaved) takes priority in admin mode
  const pagesActive = isAdmin && stagedPages !== null ? stagedPages : pages;

  // ─── Core mutations ────────────────────────────────────────────────────────

  const stageMutate = useCallback(
    (mutator: (prev: CustomPage[]) => CustomPage[]) => {
      setStagedPages((prev) => {
        const base = prev ?? pages;
        const next = mutator(base);
        // Stage for Supabase sync
        stagingStoreSet(STORAGE_KEY, next);
        stagingStoreNotify(STORAGE_KEY);
        return next;
      });
    },
    [pages],
  );

  const directMutate = useCallback(
    (mutator: (prev: CustomPage[]) => CustomPage[]) => {
      setPagesState((prev) => {
        const next = mutator(prev);
        saveCustomPages(next);
        stagingStoreSet(STORAGE_KEY, next);
        stagingStoreNotify(STORAGE_KEY);
        return next;
      });
    },
    [],
  );

  // Use staging in admin mode, direct in visitor mode
  const mutate = isAdmin ? stageMutate : directMutate;

  // ─── Actions ──────────────────────────────────────────────────────────────

  const updatePage = useCallback(
    (pageId: string, updater: (page: CustomPage) => CustomPage) => {
      mutate((prev) =>
        prev.map((p) => (p.id === pageId ? updater(p) : p)),
      );
    },
    [mutate],
  );

  const updateBlock = useCallback(
    (pageId: string, blockIdx: number, block: PageBlock) => {
      updatePage(pageId, (pg) => ({
        ...pg,
        blocks: pg.blocks.map((b, i) => (i === blockIdx ? block : b)),
        updatedAt: new Date().toISOString(),
      }));
    },
    [updatePage],
  );

  const deleteBlock = useCallback(
    (pageId: string, blockIdx: number) => {
      updatePage(pageId, (pg) => ({
        ...pg,
        blocks: pg.blocks.filter((_, i) => i !== blockIdx),
        updatedAt: new Date().toISOString(),
      }));
    },
    [updatePage],
  );

  const moveBlock = useCallback(
    (pageId: string, blockIdx: number, dir: -1 | 1) => {
      updatePage(pageId, (pg) => {
        const blocks = [...pg.blocks];
        const target = blockIdx + dir;
        if (target < 0 || target >= blocks.length) return pg;
        [blocks[blockIdx], blocks[target]] = [blocks[target], blocks[blockIdx]];
        return { ...pg, blocks, updatedAt: new Date().toISOString() };
      });
    },
    [updatePage],
  );

  const insertBlock = useCallback(
    (pageId: string, afterIdx: number, type: BlockType) => {
      const id = `block-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
      const nb = newBlock(type, id);
      updatePage(pageId, (pg) => {
        const blocks = [...pg.blocks];
        blocks.splice(afterIdx + 1, 0, nb);
        return { ...pg, blocks, updatedAt: new Date().toISOString() };
      });
    },
    [updatePage],
  );

  const updatePageTitle = useCallback(
    (pageId: string, title: string) => {
      updatePage(pageId, (pg) => ({
        ...pg,
        title,
        slug: title
          .toLowerCase()
          .replace(/[^a-z0-9\s-]/g, "")
          .trim()
          .replace(/\s+/g, "-")
          .replace(/-+/g, "-"),
        updatedAt: new Date().toISOString(),
      }));
    },
    [updatePage],
  );

  const togglePageVisibility = useCallback(
    (pageId: string) => {
      updatePage(pageId, (pg) => ({
        ...pg,
        visible: !pg.visible,
        updatedAt: new Date().toISOString(),
      }));
    },
    [updatePage],
  );

  // ─── Commit / Reset (admin only) ────────────────────────────────────────

  const commit = useCallback(() => {
    if (stagedPages !== null) {
      saveCustomPages(stagedPages);
      setPagesState(stagedPages);
      setStagedPages(null);
    }
  }, [stagedPages]);

  const reset = useCallback(() => {
    const fresh = load();
    setPagesState(fresh);
    setStagedPages(null);
  }, [load]);

  return {
    pages: pagesActive,
    allPages: pages,
    isStaged: stagedPages !== null,
    updateBlock,
    deleteBlock,
    moveBlock,
    insertBlock,
    updatePageTitle,
    togglePageVisibility,
    commit,
    reset,
  };
}

// ─── Block factory ─────────────────────────────────────────────────────────

function newBlock(type: BlockType, id: string): PageBlock {
  switch (type) {
    case "heading":
      return { id, type, content: "New Section Heading", headingLevel: "h2" };
    case "text":
      return {
        id,
        type,
        content:
          "Write your content here. Click to edit this text block.",
        textStyle: "default",
      };
    case "image":
      return {
        id,
        type,
        content: "",
        imageAlt: "",
        imageSize: "full",
        showCaption: true,
      };
    case "image-grid":
      return {
        id,
        type,
        content: "",
        images: [
          {
            id: `img-${Date.now()}-a`,
            url: "",
            alt: "",
          },
          {
            id: `img-${Date.now()}-b`,
            url: "",
            alt: "",
          },
        ],
        imageCols: 2,
      };
    case "image-detail":
      return {
        id,
        type,
        content: "",
        imageAlt: "Image",
        subContent: "Title for this image",
        ctaLabel: "Description text goes here.",
      };
    case "two-col":
      return {
        id,
        type,
        content: "Left column text goes here.",
        subContent: "Right column content goes here.",
        leftType: "text",
        rightType: "text",
      };
    case "cta":
      return {
        id,
        type,
        content: "Ready to get started?",
        subContent:
          "Join us today and be part of something special.",
        ctaLabel: "Get Started",
        ctaUrl: "/",
      };
    case "section":
      return {
        id,
        type,
        content: "Imported Section",
        sectionMeta: { layout: "cta-banner", data: {} },
      };
    case "divider":
      return { id, type, content: "" };
    default:
      return { id, type, content: "" };
  }
}